import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import NoTableEntries from "@components/DataTable/NoTableEntries";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import LoadingArrowOverlay from "@components/LoadingArrowOverlay/LoadingArrowOverlay";
import SideDrawer from "@components/SideDrawer/SideDrawer";
import { useNavigationGuard } from "@context/NavigationGuardContext";
import { FORM_FIELDS } from "@features/vessel-call/create/constants";
import type { PassengerFormValues } from "@features/vessel-call/create/schema/PassengerForm.schema";
import {
  useVesselCallFormStore,
  type Passenger,
} from "@features/vessel-call/create/store/useVesselCallForm.store";
import { templateDownloadImport } from "@features/vessel-call/create/utils/templateDownloadImport";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Nationality, PortCode } from "@services/vessel-call/types";
import { type Country } from "@services/vessel/types";
import { useTranslation } from "react-i18next";
import { useImportPassengerList } from "./useImportPassengerList.ts";

const FalForm6: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const handleDownloadTemplate = async () => {
    try {
      await templateDownloadImport({
        templateType: "passenger-list",
        fileName: "passenger-template.xlsx",
      });
    } catch (error) {
      console.error("Passenger template download failed", error);
    }
  };

  const store = useVesselCallFormStore();
  const {
    passengers,
    addPassenger,
    addPassengerDraft,
    setAddPassengerDraft,
    clearAddPassengerDraft,
    setField,
  } = store;
  const importFileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const importPassengerListMutation = useImportPassengerList();
  const { setNavigationBlocker } = useNavigationGuard();

  useEffect(() => {
    setNavigationBlocker(
      "fal-form-6-import",
      importPassengerListMutation.isPending
        ? {
            title: t("vessel-call.notifications.import-in-progress-title"),
            description: t("vessel-call.notifications.import-in-progress-description"),
            cancelButtonText: t("common.no"),
            confirmButtonText: t("common.yes"),
            onConfirm: importPassengerListMutation.abortImport,
          }
        : null,
    );

    return () => setNavigationBlocker("fal-form-6-import", null);
  }, [
    importPassengerListMutation.abortImport,
    importPassengerListMutation.isPending,
    setNavigationBlocker,
  ]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState<Passenger | null>(null);
  const openAddDrawer = () => {
    setEditingPassenger(null);
    setIsDrawerOpen(true);
  };
  const openEditDrawer = (row: Passenger) => {
    setEditingPassenger({
      ...row,
      issuingState: getCountryCodeByName.get(row.issuingState) ?? "",
      portOfEmbarkation: getPortCodeByName.get(row.portOfEmbarkation) ?? "",
      portOfDisembarkation: getPortCodeByName.get(row.portOfDisembarkation) ?? "",
    });
    setIsDrawerOpen(true);
  };
  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const openImportFileBrowser = () => {
    if (importPassengerListMutation.isPending) return;
    importFileInputRef.current?.click();
  };

  const closeImportDialog = () => {
    if (importPassengerListMutation.isPending) return;
    setSelectedImportFile(null);
  };

  const handleImportFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const [file] = Array.from(event.target.files ?? []);

    if (file) {
      setSelectedImportFile(file);
    }

    event.target.value = "";
  };

  const handleImportConfirm = () => {
    if (!selectedImportFile || importPassengerListMutation.isPending) return;

    const fileToImport = selectedImportFile;
    setPage(1);
    setSelectedImportFile(null);
    importPassengerListMutation.mutate(fileToImport);
  };

  const { options: nationalities } = useFormattedReference<Nationality>(
    REFERENCE_DATA_TYPES.NATIONALITY,
    "bff-gatepass",
  );

  const { options: countries } = useFormattedReference<Country>(
    REFERENCE_DATA_TYPES.COUNTRIES,
    "bff-gatepass",
  );

  const { options: ports } = useFormattedReference<PortCode>(
    REFERENCE_DATA_TYPES.PORTS,
    "bff-vessel-call",
  );

  const getNationalityNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    nationalities.forEach((nationality) => {
      if (!nationality?.nationality) return;
      const code = nationality.countryCode ?? nationality.nationality;
      map.set(code, nationality.nationality);
    });
    return map;
  }, [nationalities]);

  const getCountryNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    countries.forEach((country) => {
      if (!country?.countryName) return;
      const code = country.countryCode ?? country.countryName;
      map.set(code, country.countryName);
    });
    return map;
  }, [countries]);

  const getPortNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    ports.forEach((port) => {
      if (!port?.portName) return;
      const code = port.portCode ?? port.portName;
      map.set(code, port.portName);
    });
    return map;
  }, [ports]);

  const getPortCodeByName = useMemo(() => {
    const map = new Map<string, string>();
    ports.forEach((port) => {
      const code = port.portCode ?? port.portName;
      if (port.portName && code) {
        map.set(port.portName, code);
      }
    });
    return map;
  }, [ports]);

  const getCountryCodeByName = useMemo(() => {
    const map = new Map<string, string>();
    countries.forEach((country) => {
      if (country.countryName && country.countryCode) {
        map.set(country.countryName, country.countryCode);
      }
    });
    return map;
  }, [countries]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const handlePersistPassengerDraft = useCallback(
    (draft: Partial<PassengerFormValues> | null) => {
      if (!editingPassenger) {
        setAddPassengerDraft(draft);
      }
    },
    [editingPassenger, setAddPassengerDraft],
  );
  const headers: Header[] = useMemo(
    () => [
      { field: "no", columnName: t("vessel-call.create.table-headers.no"), minWidth: 64, flex: 0 },
      {
        field: "familyName",
        columnName: t("vessel-call.create.table-headers.family-name"),
        minWidth: 120,
      },
      {
        field: "givenName",
        columnName: t("vessel-call.create.table-headers.given-name"),
        minWidth: 120,
      },
      {
        field: "nationality",
        columnName: t("vessel-call.create.table-headers.nationality"),
        minWidth: 140,
      },
      {
        field: "dateOfBirth",
        columnName: t("vessel-call.create.table-headers.date-of-birth"),
        type: "date",
        minWidth: 140,
      },
      {
        field: "placeOfBirth",
        columnName: t("vessel-call.create.table-headers.place-of-birth"),
        minWidth: 160,
      },
      { field: "gender", columnName: t("vessel-call.create.table-headers.gender"), minWidth: 80 },
      {
        field: "identityDocumentNumber",
        columnName: t("vessel-call.create.table-headers.passport-number"),
        minWidth: 160,
      },
      {
        field: "issuingState",
        columnName: t("vessel-call.create.table-headers.passport-issuing-country"),
        minWidth: 160,
      },
      {
        field: "expiryDate",
        columnName: t("vessel-call.create.table-headers.passport-expiry-date"),
        type: "date",
        minWidth: 180,
      },
      {
        field: "portOfEmbarkation",
        columnName: t("vessel-call.create.table-headers.port-of-embarkation"),
        minWidth: 140,
      },
      {
        field: "portOfDisembarkation",
        columnName: t("vessel-call.create.table-headers.port-of-disembarkation"),
        minWidth: 160,
      },
      {
        field: "transitPassengerLabel",
        columnName: t("vessel-call.create.table-headers.transit-passenger"),
        minWidth: 100,
      },
    ],
    [t],
  );

  const toISO = (d?: Date | string) => {
    if (!d) return "";
    const dateObj = typeof d === "string" ? new Date(d) : d;
    return dateObj.toISOString();
  };

  const hasDuplicatePassengerPassport = (identityDocumentNumber: string, currentId?: string) => {
    const normalizedPassport = identityDocumentNumber.trim().toUpperCase();

    return passengers.some(
      (passenger) =>
        passenger.id !== currentId &&
        passenger.identityDocumentNumber.trim().toUpperCase() === normalizedPassport,
    );
  };

  const validatePassengerSubmission = (data: PassengerFormValues) => {
    if (!hasDuplicatePassengerPassport(data.identityDocumentNumber, editingPassenger?.id)) {
      return null;
    }

    return {
      identityDocumentNumber: t("form-validation.duplicate-passport-number", {
        label: t("vessel-call.create.table-headers.passport-number"),
      }),
    };
  };

  const handleAddOrEditPassenger = (data?: PassengerFormValues) => {
    if (!data) return;

    if (editingPassenger) {
      const updated: Passenger = {
        ...editingPassenger,
        familyName: data.familyName,
        givenName: data.givenName,
        nationality: data.nationality,
        dateOfBirth: toISO(data.dateOfBirth),
        placeOfBirth: data.placeOfBirth,
        gender: String(data.gender ?? ""),
        typeOfIdentityDocument: "PP",
        identityDocumentNumber: data.identityDocumentNumber,
        issuingState: data.issuingState,
        expiryDate: toISO(data.expiryDate),
        visaNumberIfAppropriate: "",
        portOfEmbarkation: data.portOfEmbarkation,
        portOfDisembarkation: data.portOfDisembarkation,
        transitPassenger: Boolean(data.transitPassenger),
      };

      useVesselCallFormStore.setState((s) => ({
        ...s,
        passengers: s.passengers.map((p) => (p.id === updated.id ? updated : p)),
      }));

      setEditingPassenger(null);
      closeDrawer();
      return;
    }

    const newPassenger: Passenger = {
      id: `passenger-${Date.now()}`,
      familyName: data.familyName,
      givenName: data.givenName,
      nationality: data.nationality,
      dateOfBirth: toISO(data.dateOfBirth),
      placeOfBirth: data.placeOfBirth,
      gender: String(data.gender ?? ""),
      typeOfIdentityDocument: "PP",
      identityDocumentNumber: data.identityDocumentNumber,
      issuingState: data.issuingState,
      expiryDate: toISO(data.expiryDate),
      visaNumberIfAppropriate: "",
      portOfEmbarkation: data.portOfEmbarkation,
      portOfDisembarkation: data.portOfDisembarkation,
      transitPassenger: Boolean(data.transitPassenger),
    };

    addPassenger(newPassenger);
    setField(FORM_FIELDS.NUMBER_OF_PASSENGERS, String(passengers.length + 1));
    setPage(1);
    clearAddPassengerDraft();
    closeDrawer();
  };

  const onRowDelete = (row: Passenger) => {
    useVesselCallFormStore.setState((s) => ({
      ...s,
      passengers: s.passengers.filter((p) => p.id !== row.id),
      [FORM_FIELDS.NUMBER_OF_PASSENGERS]: String(Math.max(0, s.passengers.length - 1)),
    }));
    setPage((prev) => Math.min(prev, Math.max(1, Math.ceil((passengers.length - 1) / pageSize))));
  };

  const onRowEdit = (row: Passenger) => {
    openEditDrawer(row);
  };

  const tableRows = useMemo(
    () =>
      [...passengers]
        .sort(
          (left, right) =>
            (left.order ?? Number.POSITIVE_INFINITY) - (right.order ?? Number.POSITIVE_INFINITY),
        )
        .map((p, idx) => ({
          ...p,
          no: p.order ?? idx + 1,
          transitPassengerLabel: p.transitPassenger ? t("common.yes") : t("common.no"),
          nationality: getNationalityNameByCode.get(p.nationality) ?? p.nationality,
          issuingState: getCountryNameByCode.get(p.issuingState) ?? p.issuingState,
          portOfEmbarkation: getPortNameByCode.get(p.portOfEmbarkation) ?? p.portOfEmbarkation,
          portOfDisembarkation:
            getPortNameByCode.get(p.portOfDisembarkation) ?? p.portOfDisembarkation,
        })),
    [passengers, t, getNationalityNameByCode, getCountryNameByCode, getPortNameByCode],
  );

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return tableRows.slice(start, end);
  }, [tableRows, page, pageSize]);

  const isEditing = Boolean(editingPassenger);

  return (
    <div className="relative p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-medium">{t("vessel-call.create.fal-form-6-title")}</h2>
        <div className="flex items-center gap-2">
          <input
            ref={importFileInputRef}
            type="file"
            hidden
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleImportFileSelected}
            data-testid="falform6-import-file-input"
          />
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={importPassengerListMutation.isPending}
            sx={{
              "borderColor": theme.palette.primary.main,
              "color": theme.palette.primary.main,
              "backgroundColor": theme.palette.common.white,
              "textTransform": "none",
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
            onClick={handleDownloadTemplate}
          >
            {t("vessel-call.create.toolbarActions.download-template")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DescriptionOutlinedIcon />}
            disabled={importPassengerListMutation.isPending}
            sx={{
              "borderColor": theme.palette.primary.main,
              "color": theme.palette.primary.main,
              "backgroundColor": theme.palette.common.white,
              "textTransform": "none",
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
            onClick={openImportFileBrowser}
            data-testid="falform6-import-button"
          >
            {t("vessel-call.create.toolbarActions.import-xlsx")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ControlPointIcon />}
            disabled={importPassengerListMutation.isPending}
            onClick={openAddDrawer}
            sx={{
              "borderColor": theme.palette.primary.main,
              "color": theme.palette.primary.main,
              "backgroundColor": theme.palette.common.white,
              "textTransform": "none",
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            {t("vessel-call.create.toolbarActions.add-passenger")}
          </Button>
        </div>
      </div>

      {passengers.length === 0 ? (
        <div className="mb-24">
          <NoTableEntries
            dataName={t("vessel-call.create.fal-form-6-member")}
            actionName={t("vessel-call.create.toolbarActions.add-passenger")}
          />
        </div>
      ) : (
        <CustomDataTable
          title=""
          actionName={t("vessel-call.create.toolbarActions.add-passenger")}
          rows={pagedRows}
          headers={headers}
          page={page}
          pageSize={pageSize}
          totalCount={tableRows.length}
          navigationLinkBase=""
          filters={[]}
          onApplyFilters={() => {}}
          availableFields={{}}
          onPageChange={(p: number, ps?: number) => {
            setPage(p);
            if (ps && ps !== pageSize) setPageSize(ps);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          hideFilters={true}
          actionColumn={{
            onEditClick: onRowEdit,
            onDeleteClick: onRowDelete,
            width: 140,
          }}
        />
      )}

      <LoadingArrowOverlay loading={importPassengerListMutation.isPending} />
      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        title={
          isEditing
            ? t("vessel-call.create.toolbarActions.edit-passenger")
            : t("vessel-call.create.toolbarActions.add-passenger")
        }
        description={
          isEditing
            ? t("vessel-call.create.toolbarActions.edit-passenger")
            : t("vessel-call.create.toolbarActions.add-passenger")
        }
        submitButtonLabel={
          isEditing
            ? t("vessel-call.create.toolbarActions.edit-passenger")
            : t("vessel-call.create.toolbarActions.add-passenger")
        }
        type={isEditing ? "editPassenger" : "addPassenger"}
        notesLabel={t("common.notes")}
        onSubmit={handleAddOrEditPassenger}
        passengerInitialValues={
          isEditing
            ? {
                familyName: editingPassenger!.familyName,
                givenName: editingPassenger!.givenName,
                nationality: editingPassenger!.nationality,
                dateOfBirth: editingPassenger!.dateOfBirth
                  ? new Date(editingPassenger!.dateOfBirth)
                  : undefined,
                placeOfBirth: editingPassenger!.placeOfBirth,
                gender: editingPassenger!.gender,
                identityDocumentNumber: editingPassenger!.identityDocumentNumber,
                issuingState: editingPassenger!.issuingState,
                expiryDate: editingPassenger!.expiryDate
                  ? new Date(editingPassenger!.expiryDate)
                  : undefined,
                portOfEmbarkation: editingPassenger!.portOfEmbarkation,
                portOfDisembarkation: editingPassenger!.portOfDisembarkation,
                transitPassenger: editingPassenger!.transitPassenger,
              }
            : (addPassengerDraft ?? undefined)
        }
        validatePassengerSubmission={validatePassengerSubmission}
        onPersistDraft={handlePersistPassengerDraft}
      />

      <GenericDialog
        open={Boolean(selectedImportFile)}
        title={t("vessel-call.create.fal-form-6.import-dialog-title")}
        elementContent={
          <div className="space-y-3">
            <p>
              {t("vessel-call.create.fal-form-6.import-dialog-description-prefix")}{" "}
              <strong>{selectedImportFile?.name ?? ""}</strong>{" "}
              {t("vessel-call.create.fal-form-6.import-dialog-description-suffix")}
            </p>
            {passengers.length > 0 && (
              <p className="font-medium text-amber-700">
                {t("vessel-call.create.fal-form-6.import-dialog-overwrite-warning")}
              </p>
            )}
          </div>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("vessel-call.create.fal-form-6.import-dialog-confirm")}
        handleCloseDialog={closeImportDialog}
        confirmAction={handleImportConfirm}
        testId="falform6-import-confirm-dialog"
      />
    </div>
  );
};

export default FalForm6;
